import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

const box = "bg-[#0B0D14] border border-[#1F2937] rounded px-3 py-2 text-sm w-full";
const btn = "mono text-[10px] uppercase tracking-widest px-4 py-2 rounded-full disabled:opacity-40";

function Preview({ html }) {
  return html ? <iframe title="preview" srcDoc={html} data-testid="email-preview" className="w-full h-[560px] mt-3 rounded-lg border border-blue-900/50 bg-[#050914]" /> : null;
}

function LaunchCard() {
  const [info, setInfo] = useState(null);
  const [subject, setSubject] = useState("");
  const [text, setText] = useState("");
  const [html, setHtml] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [kind, setKind] = useState("update");
  useEffect(() => { api.get("/admin/store-emails/launch").then((r) => { setInfo(r.data); setSubject(r.data.templates.update.subject); setText(r.data.templates.update.text); }); }, []);
  const pick = (k) => { setKind(k); setSubject(info.templates[k].subject); setText(info.templates[k].text); setHtml(""); };
  const body = { kind, subject, text };
  const preview = async () => setHtml((await api.post("/admin/store-emails/launch/preview", body)).data);
  const test = async () => { const r = await api.post("/admin/store-emails/launch/test", body); setMsg(r.data.ok ? `Test sent to ${r.data.to}` : "Test failed - check Resend"); };
  const send = async () => {
    if (!window.confirm(`Send "${subject}" to ${info.recipients} people now? This can't be undone.`)) return;
    setBusy(true);
    const r = await api.post("/admin/store-emails/launch/send", body);
    setMsg(`Sending to ${r.data.recipients} people in the background (about 2 per second).`);
  };
  if (!info) return null;
  return (
    <section data-testid="launch-email-card" className="border border-[#FBBF24]/40 rounded-xl p-5">
      <div className="mono text-[10px] uppercase tracking-widest text-[#FBBF24]">Announcement emails</div>
      <div className="flex gap-2 mt-2">
        {[["update", "Pre-launch update"], ["launch", "Launch day"]].map(([k, l]) => (
          <button key={k} onClick={() => pick(k)} data-testid={`announce-kind-${k}`}
            className={`${btn} ${kind === k ? "bg-[#FBBF24] text-[#050914]" : "border border-blue-900/60 text-[#94A3B8]"}`}>{l}</button>
        ))}
      </div>
      <div className="text-sm text-[#94A3B8] mt-1" data-testid="launch-recipients">
        Goes to <b className="text-white">{info.recipients}</b> real sign-ups: {info.audience_breakdown.waitlist_only} waitlist-only + {info.audience_breakdown.accounts} accounts
        ({info.audience_breakdown.both} of those are on both, counted once), minus unsubscribes.
        {info.last && <> Last: {info.last.status} {info.last.sent_count != null ? `(${info.last.sent_count} sent)` : ""}.</>}
      </div>
      <input value={subject} onChange={(e) => setSubject(e.target.value)} className={`${box} mt-3`} data-testid="launch-subject" />
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={8} className={`${box} mt-2`} data-testid="launch-text" />
      <div className="flex flex-wrap gap-2 mt-3">
        <button onClick={preview} className={`${btn} border border-blue-900/60`} data-testid="launch-preview-btn">Preview</button>
        <button onClick={test} className={`${btn} border border-[#38BDF8] text-[#38BDF8]`} data-testid="launch-test-btn">Send test to me</button>
        <button onClick={send} disabled={busy} className={`${btn} bg-[#FBBF24] text-[#050914]`} data-testid="launch-send-btn">Send to everyone</button>
      </div>
      {msg && <div className="text-xs text-[#34D399] mt-2" data-testid="launch-msg">{msg}</div>}
      <Preview html={html} />
    </section>
  );
}

function Digest({ d, reload }) {
  const [subject, setSubject] = useState(d.subject);
  const [text, setText] = useState(d.text);
  const [html, setHtml] = useState("");
  const [msg, setMsg] = useState("");
  const draft = d.status === "draft";
  const save = async () => { await api.post(`/admin/store-emails/weekly/${d._id}`, { subject, text }); setMsg("Saved"); };
  const preview = async () => { if (draft) await save(); setHtml((await api.get(`/admin/store-emails/weekly/${d._id}/preview`)).data); };
  const send = async () => {
    if (!window.confirm("Send this weekly email to everyone now?")) return;
    await save(); await api.post(`/admin/store-emails/weekly/${d._id}/send`); setMsg("Sending…"); reload();
  };
  return (
    <div data-testid={`digest-${d._id}`} className="border border-blue-900/40 rounded-lg p-4 mt-3">
      <div className="mono text-[10px] uppercase tracking-widest text-[#94A3B8]">{d._id} · <span className={draft ? "text-[#FBBF24]" : "text-[#34D399]"}>{d.status}</span>{d.sent_count != null ? ` · ${d.sent_count} sent` : ""}</div>
      <input value={subject} onChange={(e) => setSubject(e.target.value)} disabled={!draft} className={`${box} mt-2`} data-testid="digest-subject" />
      <textarea value={text} onChange={(e) => setText(e.target.value)} disabled={!draft} rows={8} className={`${box} mt-2`} data-testid="digest-text" />
      <div className="flex flex-wrap gap-2 mt-2">
        {draft && <button onClick={save} className={`${btn} border border-blue-900/60`} data-testid="digest-save">Save edits</button>}
        <button onClick={preview} className={`${btn} border border-[#38BDF8] text-[#38BDF8]`} data-testid="digest-preview">Preview</button>
        {draft && <button onClick={send} className={`${btn} bg-[#34D399] text-[#050914]`} data-testid="digest-send">Approve & send</button>}
      </div>
      {msg && <div className="text-xs text-[#34D399] mt-2">{msg}</div>}
      <Preview html={html} />
    </div>
  );
}

function WeeklyCard() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [mfx, setMfx] = useState("");
  const [msg, setMsg] = useState("");
  const load = () => api.get("/admin/store-emails/weekly").then((r) => { setD(r.data); setEmail(r.data.showcase.email || ""); setMfx(r.data.showcase.myfxbook_url || ""); });
  useEffect(() => { load(); }, []);
  const gen = async () => { setBusy(true); try { await api.post("/admin/store-emails/weekly/generate"); await load(); } finally { setBusy(false); } };
  const saveShow = async () => {
    try { await api.post("/admin/store-showcase", { email, myfxbook_url: mfx }); setMsg("Showcase saved"); } catch (e) { setMsg(e?.response?.data?.detail || "Failed"); }
  };
  if (!d) return null;
  return (
    <section data-testid="weekly-email-card" className="border border-[#34D399]/40 rounded-xl p-5">
      <div className="mono text-[10px] uppercase tracking-widest text-[#34D399]">Weekly founder email</div>
      <div className="text-sm text-[#94A3B8] mt-1">
        Every Monday 8am UK the AI writes a draft from your demo results and emails it to you with an <b>Approve & send</b> button.
        Nothing goes out until you approve. Goes to <b className="text-white" data-testid="weekly-recipients">{d.recipients}</b> people (waitlist + opted-in accounts).
      </div>
      <div className="grid md:grid-cols-2 gap-2 mt-3">
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your BotGenomix account email (demo results)" className={box} data-testid="showcase-email" />
        <input value={mfx} onChange={(e) => setMfx(e.target.value)} placeholder="Myfxbook URL" className={box} data-testid="showcase-myfxbook" />
      </div>
      <div className="flex flex-wrap gap-2 mt-2 items-center">
        <button onClick={saveShow} className={`${btn} border border-blue-900/60`} data-testid="showcase-save">Save showcase account</button>
        <button onClick={gen} disabled={busy} className={`${btn} bg-[#34D399] text-[#050914]`} data-testid="weekly-generate">{busy ? "Writing…" : "Generate a draft now"}</button>
        {msg && <span className="text-xs text-[#34D399]">{msg}</span>}
      </div>
      {d.digests.map((x) => <Digest key={x._id + x.status} d={x} reload={load} />)}
    </section>
  );
}

function SalesToggle() {
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/store/status").then((r) => setOpen(r.data.sales_open)); }, []);
  const flip = async () => {
    if (!window.confirm(open ? "Close the store to new subscriptions (pre-launch mode)?" : "OPEN the store for real subscriptions now?")) return;
    setOpen((await api.post("/admin/store-sales", { open: !open })).data.sales_open);
  };
  if (open === null) return null;
  return (
    <section data-testid="sales-toggle-card" className={`border rounded-xl p-5 ${open ? "border-[#34D399]/50" : "border-[#F87171]/50"}`}>
      <div className="mono text-[10px] uppercase tracking-widest text-[#94A3B8]">Store sales</div>
      <div className="flex flex-wrap items-center justify-between gap-3 mt-1">
        <div className="text-sm" data-testid="sales-status">
          {open ? <b className="text-[#34D399]">OPEN - customers can subscribe</b> : <b className="text-[#F87171]">PRE-LAUNCH - "buy" buttons collect launch-list emails instead</b>}
        </div>
        <button onClick={flip} data-testid="sales-toggle-btn" className={`${btn} ${open ? "border border-[#F87171] text-[#F87171]" : "bg-[#34D399] text-[#050914]"}`}>
          {open ? "Close sales" : "Open the store"}
        </button>
      </div>
    </section>
  );
}

export default function StoreEmails() {
  return (
    <div data-testid="store-emails-page" className="min-h-screen bg-[#050914] text-[#F1F5F9]">
      <div className="max-w-[1000px] mx-auto px-6 py-10 space-y-6">
        <Link to="/admin-mt5" data-testid="emails-back" className="mono text-[10px] uppercase tracking-widest text-[#94A3B8]">← MT5 Lab</Link>
        <h1 className="text-3xl font-medium">Store emails</h1>
        <SalesToggle />
        <LaunchCard />
        <WeeklyCard />
      </div>
    </div>
  );
}
